import IconChevronDown from '@/assets/icons/font_awesome/chevron-down-solid.svg?react';
import { UI_PRIMITIVE__button } from '@/components/ui/UI_PRIMITIVE__button';
import { UI_PRIMITIVE__dropdown_menu } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu';
import { UI_PRIMITIVE__dropdown_menu_content } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu_content';
import { UI_PRIMITIVE__dropdown_menu_item } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu_item';
import { UI_PRIMITIVE__dropdown_menu_trigger } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu_trigger';
import NotifierBorder from '@/components/ui/notifier/notifier-border';
import { ENUM__component_type } from '@/enums/ENUM__component_type';
import { HOOK__notifier } from '@/hooks/HOOK__notifier';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_slice_ml_asset, use_slice_search, use_slice_workspace } from '@/hooks/store/use_slices';
import i18n_page_search from '@/locales/keys/i18n_page_search.json';
import { BarWorkspaceEntityOrBarLine } from '@/pages/_base/views/BarWorkspaceEntityOrBarLine';
import { PLACEHOLDER_bar_line } from '@/pages/_base/views/PLACEHOLDER_bar_line';
import { ENUM__i18n_namespace } from '@/services/i18n/ENUM__i18n_namespace';
import { remote_search_update } from '@/state/search/remote_search_update';
import { ENUM__workspace_entity } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity';
import { ENUM__workspace_entity_feature } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity_feature';
import { useTranslation } from 'react-i18next';

export function SDiffusionModelDropdown() {
  const { t } = useTranslation(ENUM__i18n_namespace.PAGE_SEARCH);
  const dispatch = use_dispatch();

  const { search_current } = use_slice_search(['search_current']);
  const { current_workspace_user } = use_slice_workspace(['current_workspace_user']);
  const { ml_assets_map } = use_slice_ml_asset(['ml_assets_map']);

  const component_id = `${ENUM__component_type.PAGE_SEARCH_DIFFUSION_MODEL_DROPDOWN_TRIGGER}_${search_current._id}`;
  const notifier = HOOK__notifier(component_id, ENUM__workspace_entity.ENTITY_SEARCH);

  const trigger_id = 'dropdown_setting_diffusion_model';

  /*
   * EVENTS
   */

  async function on_item_selected(value_string: string) {
    if (search_current.diffusion_models.some(item => item.value == value_string)) return;
    if (search_current.diffusion_models.is_empty() && !value_string) return;

    let diffusion_models_new = value_string ? [new DTO_GENERATION_SETTING__string(value_string)] : [];

    let search_new = search_current.clone({ diffusion_models: diffusion_models_new });

    await dispatch(remote_search_update(search_new));

    await notifier.send_action();
  }

  /*
   * METHODS
   */

  function is_disabled() {
    return !current_workspace_user.is_feature_pertmitted(ENUM__workspace_entity_feature.ENTITY_SEARCH_UPDATE);
  }

  function user_can_view() {
    if (search_current.is_stub()) return false;

    return search_current.is_shared || current_workspace_user.is_feature_pertmitted(ENUM__workspace_entity_feature.ENTITY_SEARCH_VIEW);
  }

  /*
   * RENDERS
   */

  function render_items() {
    let diffusion_models_settings = Array.from(ml_assets_map.values())
      .filter(item => item.is_free_for_rent)
      .map(item => new DTO_GENERATION_SETTING__string(item._id));

    let diffusion_models = [DTO_GENERATION_SETTING__string.CREATE_STUB(), ...diffusion_models_settings];

    return diffusion_models.map(item => {
      return (
        <UI_PRIMITIVE__dropdown_menu_item
          key={item.value}
          value={item.value}
          {...{ on_item_selected }}
          className={`w-full rounded-lg transition-colors duration-300 cursor-pointer hover:bg-accent text-left flex justify-between
         ${search_current.has_diffusion_model(item) ? 'bg-accent' : ''}
         `}
        >
          <span className="max-w-28 min-w-28">{item.is_stub() ? t(i18n_page_search.sidebar.dropdown.diffusion_model.items.ALL) : item.value}</span>
        </UI_PRIMITIVE__dropdown_menu_item>
      );
    });
  }

  function render_current_value() {
    return search_current.diffusion_models.is_empty()
      ? t(i18n_page_search.sidebar.dropdown.diffusion_model.items.ALL)
      : search_current.diffusion_models[0].value;
  }

  /*
   * HTML
   */

  if (!user_can_view()) return <PLACEHOLDER_bar_line />;

  function render_view() {
    return (
      <>
        <UI_PRIMITIVE__dropdown_menu>
          <UI_PRIMITIVE__dropdown_menu_trigger asChild>
            <UI_PRIMITIVE__button id={trigger_id} className="relative w-full justify-start h-9" variant="outline-on-bar" disabled={is_disabled()}>
              <NotifierBorder notifier={notifier} />
              <span className="w-full text-left">{t(i18n_page_search.sidebar.dropdown.diffusion_model.title)}:</span>
              <span className="max-w-28 min-w-28 text-right truncate">{render_current_value()}</span>
              <IconChevronDown className="!w-5 !h-5 !text-component-item-foreground" />
            </UI_PRIMITIVE__button>
          </UI_PRIMITIVE__dropdown_menu_trigger>
          <UI_PRIMITIVE__dropdown_menu_content trigger_id={trigger_id} className=" rounded-lg">
            {render_items()}
          </UI_PRIMITIVE__dropdown_menu_content>
        </UI_PRIMITIVE__dropdown_menu>
      </>
    );
  }

  return <BarWorkspaceEntityOrBarLine view={render_view()} entity={search_current} required_permission={ENUM__workspace_entity_feature.ENTITY_SEARCH_VIEW} />;
}
